import type { BreadcrumbItem, NavigationItem } from "./types"

type GetBreadcrumbTrailOptions = {
	leafLabel?: string | null
}

/**
 * Check if a navigation item is active based on current route
 * @param item - Navigation item to check
 * @param currentPath - Current route path from TanStack Router
 * @returns true if the item's route matches the current path
 */
export function isNavigationItemActive(
	item: NavigationItem,
	currentPath: string,
): boolean {
	if (!item.route) return false
	// Normalize paths by removing trailing slashes for comparison
	const normalizedCurrentPath = currentPath.replace(/\/$/, "")
	const normalizedRoute = item.route.replace(/\/$/, "")
	return (
		normalizedCurrentPath === normalizedRoute ||
		normalizedCurrentPath.startsWith(`${normalizedRoute}/`)
	)
}

/**
 * Find active navigation item in navigation tree
 * Recursively searches through navigation items and their children
 * @param items - Array of navigation items to search
 * @param currentPath - Current route path from TanStack Router
 * @returns The active navigation item, or null if none found
 */
export function findActiveNavigationItem(
	items: NavigationItem[],
	currentPath: string,
): NavigationItem | null {
	for (const item of items) {
		if (isNavigationItemActive(item, currentPath)) {
			return item
		}
		if (item.children) {
			const found = findActiveNavigationItem(item.children, currentPath)
			if (found) return found
		}
	}
	return null
}

function findNavigationBreadcrumbTrail(
	items: NavigationItem[],
	currentPath: string,
): BreadcrumbItem[] {
	for (const parent of items) {
		if (parent.children) {
			for (const child of parent.children) {
				if (isNavigationItemActive(child, currentPath)) {
					return [
						{ label: parent.label },
						{ label: child.label, route: child.route },
					]
				}
			}
			continue
		}

		if (isNavigationItemActive(parent, currentPath)) {
			return [{ label: parent.label, route: parent.route }]
		}
	}

	return []
}

/**
 * Build a breadcrumb trail for the current route (section → list → sub-page).
 */
export function getBreadcrumbTrail(
	route: string,
	navigationConfig: NavigationItem[],
	options?: GetBreadcrumbTrailOptions,
): BreadcrumbItem[] {
	const normalizedRoute = route.replace(/\/$/, "") || "/"

	if (normalizedRoute === "/" || normalizedRoute === "") {
		return []
	}

	const navTrail = findNavigationBreadcrumbTrail(
		navigationConfig,
		normalizedRoute,
	)
	const subRouteLabel = options?.leafLabel ?? null

	if (navTrail.length === 0) {
		const item = findActiveNavigationItem(navigationConfig, normalizedRoute)
		if (!item) {
			return [{ label: normalizedRoute }]
		}

		return item.route
			? [{ label: item.label, route: item.route }]
			: [{ label: item.label }]
	}

	if (!subRouteLabel) {
		const last = navTrail[navTrail.length - 1]
		return [...navTrail.slice(0, -1), { label: last.label }]
	}

	return [...navTrail, { label: subRouteLabel }]
}

/**
 * Get breadcrumb label for a given route
 * Derives breadcrumb text from navigation configuration
 * @param route - Route path to get breadcrumb for
 * @param navigationConfig - Navigation configuration array
 * @returns Breadcrumb label, or default label for root path, or route path if not found
 */
export function getBreadcrumbLabel(
	route: string,
	navigationConfig: NavigationItem[],
): string {
	// Handle root path
	if (route === "/" || route === "") {
		return ""
	}

	const trail = getBreadcrumbTrail(route, navigationConfig)
	const last = trail[trail.length - 1]
	return last?.label || route
}
